import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { List, Calendar, CreditCard, X, MoreVertical, MessageSquare, Send, Clock, XCircle, Video, Phone, Building2, IndianRupee, Coins, CheckCircle2, Check, Activity, Repeat, UserX, FileText, Pencil, PlayCircle, Undo2 } from "lucide-react";
import SeriesModal from "../../../components/booking/SeriesModal";
import DashboardLayout from "../../../layouts/DashboardLayout";
import api from "../../../api";
import { createPortal } from "react-dom";
import CalendarView from "../../../components/CalendarView";
import VideoCallModal from "./VideoCallModal";
import useFeatures from "../../../hooks/useFeatures";
import { usePeriodFilter, PeriodPicker, ALL_UPCOMING_OPTION, PERIOD_OPTIONS } from "../../../components/dashboard/financePeriod";

const APPOINTMENT_PERIOD_OPTIONS = [ALL_UPCOMING_OPTION, ...PERIOD_OPTIONS];

// Visit type as an icon (in person / video / voice) so the Type & Payment cell stays narrow.
function VisitTypeIcon({ appt }) {
  const online = appt.appttype === "Online";
  const Icon = !online ? Building2 : appt.audio_only ? Phone : Video;
  const label = !online ? "In person" : appt.audio_only ? "Voice call" : "Video call";
  return (
    <span title={label} aria-label={label} className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-secondary-100 text-secondary-700 shrink-0">
      <Icon className="w-3.5 h-3.5" aria-hidden="true" />
    </span>
  );
}

export default function Appointment() {
  const [appointments, setAppointments] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [viewMode, setViewMode] = useState("tabular");
  const [search, setSearch] = useState("");
  const [doctorFilter, setDoctorFilter] = useState("");
  const [showCancelled, setShowCancelled] = useState(false);
  const { period, setPeriod, customStart, setCustomStart, customEnd, setCustomEnd, range } = usePeriodFilter({ period: "all_upcoming" });
  const [loading, setLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const [menuAnchor, setMenuAnchor] = useState(null); // button's own rect, for re-measuring once the menu mounts
  const menuRef = useRef(null);
  const [selectedPaymentAppt, setSelectedPaymentAppt] = useState(null);
  const [videoCallAppt, setVideoCallAppt] = useState(null);
  const [seriesId, setSeriesId] = useState(null);
  const [startConsultationAppt, setStartConsultationAppt] = useState(null);
  const [startingConsultation, setStartingConsultation] = useState(false);
  const [collectMethod, setCollectMethod] = useState("");
  const [collectReference, setCollectReference] = useState("");
  const [collectAmount, setCollectAmount] = useState("");
  const [collecting, setCollecting] = useState(false);
  const [collectError, setCollectError] = useState("");
  const [refundModal, setRefundModal] = useState(null); // { appt, refundAmount }
  const [refundProcessing, setRefundProcessing] = useState(false);
  const [refundToast, setRefundToast] = useState(null); // { message, type }
  const [selectedMessagesAppt, setSelectedMessagesAppt] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // ?highlight=<appointment id> (from the Today page): scroll to that row and
  // mark it so the clinician sees which appointment to open.
  const [highlightId, setHighlightId] = useState(() => Number(searchParams.get("highlight")) || null);
  useEffect(() => {
    if (!highlightId || !filtered.some((a) => a.id === highlightId)) return;
    // The table (desktop) and the cards (phones) both exist; scroll to the visible one.
    const el = [document.getElementById(`appt-row-${highlightId}`), document.getElementById(`appt-card-${highlightId}`)]
      .find((n) => n && n.offsetParent !== null);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    const t = setTimeout(() => setHighlightId(null), 8000);
    return () => clearTimeout(t);
  }, [highlightId, filtered]); // eslint-disable-line
  const auth = JSON.parse(localStorage.getItem("auth") || "{}");

  // ✅ Fetch appointments
  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (doctorFilter.trim()) params.doctor_name = doctorFilter.trim();
      if (range) {
        params.start_date = range.start;
        if (range.end) params.end_date = range.end; // "All Upcoming" has no end bound
      }
      if (showCancelled) params.include_cancelled = "true";

      const res = await api.fetchAppointments(params); // GET /appointments/
      let data = res.data || [];

      // If doctor → show only their appointments
      if (auth.role === "Doctor") {
        data = data.filter((a) => a.doctor_id === auth.user_id);
      }

      setAppointments(data);
      setFiltered(data); // filtered and appointments are now the same since backend handles filtering
    } catch (err) {
      console.error("Failed to fetch appointments", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleMenu = (event, id) => {
    if (openMenuId === id) {
      setOpenMenuId(null);
      return;
    }
    // Get the position of the button relative to the viewport. The menu's
    // real height depends on which items render for this row (a 2-item
    // "Declined" menu vs. the ~7-item full action menu), which isn't known
    // until it actually mounts - so this is only a same-paint-frame guess
    // to avoid a visible jump; the layout effect below corrects it against
    // the menu's real measured height right after mount.
    const rect = event.currentTarget.getBoundingClientRect();
    const right = Math.max(16, window.innerWidth - rect.right);
    const top = rect.bottom + 4;
    const estimatedHeight = 190;
    const adjustedTop = (top + estimatedHeight > window.innerHeight && rect.top > estimatedHeight)
      ? rect.top - estimatedHeight - 4
      : top;

    setMenuAnchor({ top: rect.top, bottom: rect.bottom, right });
    setMenuPosition({
      top: adjustedTop,
      right: right
    });
    setOpenMenuId(id);
  };

  // Snaps the menu to the clicked row's button using its actual rendered
  // height instead of the hardcoded estimate above - fixes the menu
  // floating rows away from where it was opened when the real content is
  // much shorter (or taller) than the guess.
  useLayoutEffect(() => {
    if (!openMenuId || !menuAnchor || !menuRef.current) return;
    const height = menuRef.current.getBoundingClientRect().height;
    const margin = 4;
    const fitsBelow = menuAnchor.bottom + margin + height <= window.innerHeight;
    const top = fitsBelow
      ? menuAnchor.bottom + margin
      : Math.max(margin, menuAnchor.top - height - margin);
    setMenuPosition((prev) =>
      prev.top === top && prev.right === menuAnchor.right ? prev : { top, right: menuAnchor.right }
    );
  }, [openMenuId, menuAnchor]);

  const handleCloseMenu = () => {
    setOpenMenuId(null);
  };

  // Reset the collect-payment form whenever a different (or no) appointment
  // is selected, so a stale method/reference from one appointment never
  // leaks into the next one's modal.
  useEffect(() => {
    setCollectMethod("");
    setCollectReference("");
    setCollectError("");
    setCollectAmount(selectedPaymentAppt?.fee != null ? String(selectedPaymentAppt.fee) : "");
  }, [selectedPaymentAppt?.id]);

  const handleCollectPayment = async () => {
    if (!selectedPaymentAppt || !collectMethod) return;
    const parsedAmount = parseFloat(collectAmount);
    if (isNaN(parsedAmount) || parsedAmount < 0) {
      setCollectError("Enter a valid amount.");
      return;
    }
    setCollecting(true);
    setCollectError("");
    try {
      const res = await api.collectAppointmentPayment(selectedPaymentAppt.id, {
        collection_method: collectMethod,
        reference: collectReference.trim() || undefined,
        amount: parsedAmount,
      });
      const patch = {
        payment_status: res.data.payment_status,
        payment_mode: res.data.payment_mode,
        fee: res.data.fee,
        collected_payment_method: res.data.collected_payment_method,
        collected_payment_reference: res.data.collected_payment_reference,
        collected_by: res.data.collected_by,
        collected_by_name: auth.user_name || null,
        collected_at: res.data.collected_at,
      };
      setSelectedPaymentAppt((prev) => ({ ...prev, ...patch }));
      setAppointments((prev) => prev.map((a) => (a.id === res.data.id ? { ...a, ...patch } : a)));
      setFiltered((prev) => prev.map((a) => (a.id === res.data.id ? { ...a, ...patch } : a)));
      fetchAppointments(); // background refresh for the fully server-resolved shape
    } catch (err) {
      console.error("Failed to record payment", err);
      setCollectError(err.response?.data?.error || "Failed to record payment.");
    } finally {
      setCollecting(false);
    }
  };

  // Opens the Messages modal for an appointment and loads its thread -
  // listing the thread also marks the patient's messages read server-side,
  // so we optimistically clear this row's unread badge locally rather than
  // waiting for the next poll/refetch.
  const openMessagesModal = async (appt) => {
    handleCloseMenu();
    setSelectedMessagesAppt(appt);
    setNewMessage("");
    setMessagesLoading(true);
    try {
      const res = await api.getAppointmentMessages(appt.id);
      setMessages(res.data || []);
      const clearUnread = (list) => list.map((a) => (a.id === appt.id ? { ...a, unread_patient_messages: 0 } : a));
      setAppointments(clearUnread);
      setFiltered(clearUnread);
    } catch (err) {
      console.error("Failed to load messages", err);
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleSendMessage = async () => {
    const body = newMessage.trim();
    if (!body || !selectedMessagesAppt) return;
    setSendingMessage(true);
    try {
      const res = await api.sendAppointmentMessage(selectedMessagesAppt.id, { message: body });
      setMessages((prev) => [...prev, res.data]);
      setNewMessage("");
    } catch (err) {
      console.error("Failed to send message", err);
    } finally {
      setSendingMessage(false);
    }
  };

  const insertSoapTemplate = () => {
    setNewMessage((prev) => (prev ? prev : "Subjective: \nObjective: \nAssessment: \nPlan: "));
  };

  // A notification-bell click lands here with ?open_messages=<id> - fetches
  // that appointment directly rather than looking for it inside the
  // currently-loaded `appointments` list, since that list is filtered
  // (default view is "All Upcoming") and would never contain an older
  // appointment a patient just messaged on - this previously meant the
  // modal silently failed to open with no feedback at all whenever the
  // notification pointed at a non-upcoming appointment. Always routes here
  // rather than into a new consultation, since starting one is date-gated
  // to the appointment's own day (confirmStartConsultation above) and a
  // message notification must never be blocked by that.
  useEffect(() => {
    const openId = searchParams.get("open_messages");
    if (!openId) return;

    const next = new URLSearchParams(searchParams);
    next.delete("open_messages");
    setSearchParams(next, { replace: true });

    (async () => {
      try {
        const res = await api.getAppointment(openId);
        openMessagesModal(res.data);
      } catch (err) {
        console.error("Failed to load appointment for message notification", err);
      }
    })();
  }, [searchParams]);

  // "Patient is waiting" alert (?start_consultation=<id>): open the same
  // Start Consultation confirmation as the row menu, so the same checks and
  // flow apply (createConsultation, then the New Consultation page).
  useEffect(() => {
    const startId = searchParams.get("start_consultation");
    if (!startId) return;
    const next = new URLSearchParams(searchParams);
    next.delete("start_consultation");
    setSearchParams(next, { replace: true });
    (async () => {
      try {
        const res = await api.getAppointment(startId);
        setStartConsultationAppt(res.data);
      } catch (err) {
        console.error("Failed to load appointment for the patient-waiting alert", err);
      }
    })();
  }, [searchParams]);

  // ✅ Server-side filtering with debounce
  useEffect(() => {
    if (period === "custom" && (!range || range.end < range.start)) return; // incomplete/invalid custom range, wait for it to be fixed
    const delayDebounceFn = setTimeout(() => {
      fetchAppointments();
    }, 300); // 300ms debounce

    return () => clearTimeout(delayDebounceFn);
  }, [search, doctorFilter, period, customStart, customEnd, showCancelled]);

  // Attendance (feature "attendance"): a no-show can be recorded once the
  // session time has started and no notes exist; it can also be undone.
  const features = useFeatures();
  const CANCEL_REASONS = ["Patient asked to cancel", "Clinician unavailable", "Clinic closed", "Booked by mistake", "Other"];
  const hasStarted = (a) => a.date && a.time && new Date(`${a.date}T${a.time}`) <= new Date();
  const canMarkNoShow = (a) => features.has("attendance") && !a.has_consultation && !a.is_cancelled
    && a.confirmation_status === "Confirmed" && hasStarted(a) && a.attendance !== "no_show";
  const setAttendance = async (a, value) => {
    handleCloseMenu();
    if (value === "no_show" && !window.confirm(`Record that ${a.patient_name} didn't attend? They'll get a message inviting them to rebook.`)) return;
    try {
      await api.setAppointmentAttendance(a.id, value);
      setAppointments((prev) => prev.map((x) => (x.id === a.id ? { ...x, attendance: value } : x)));
    } catch (err) {
      alert(err.response?.data?.error || "Couldn't update attendance.");
    }
  };

  // ✅ Actions
  const openCancelModal = (appt) => {
    handleCloseMenu();
    if (appt?.has_consultation) {
      alert("This appointment cannot be cancelled because a consultation already exists for it.");
      return;
    }
    const isPrepaid = appt?.payment_status === 'Prepaid';
    setRefundModal({
      appt,
      isPrepaid,
      refundAmount: isPrepaid ? (appt.fee || 0) : 0
    });
  };

  const handleConfirmCancel = async () => {
    if (!refundModal) return;
    const { appt, isPrepaid, refundAmount } = refundModal;
    setRefundProcessing(true);
    try {
      const reason = refundModal.reason === "Other" ? (refundModal.reasonText || "").trim() : (refundModal.reason || "");
      const body = { ...(isPrepaid ? { refund_amount: parseFloat(refundAmount) } : {}), ...(reason ? { reason } : {}) };
      const res = await api.cancelAppointment(appt.id, body);
      // The list renders `filtered`, so update both. With "Show Cancelled" on, keep the row and mark it.
      const drop = (prev) => showCancelled
        ? prev.map((a) => (a.id === appt.id ? { ...a, is_cancelled: true, cancelled_by: "clinic", cancel_reason: reason || a.cancel_reason } : a))
        : prev.filter((a) => a.id !== appt.id);
      setAppointments(drop);
      setFiltered(drop);
      setRefundModal(null);
      if (isPrepaid && res.data?.refund?.refund_initiated) {
        const r = res.data.refund;
        setRefundToast({
          message: `Appointment cancelled. ₹${r.amount} refund initiated${r.is_sandbox ? ' (sandbox)' : ''}. Ref: ${r.refund_id}`,
          type: 'success'
        });
      } else if (isPrepaid && res.data?.refund?.refund_initiated === false) {
        setRefundToast({ message: `Appointment cancelled, but refund failed: ${res.data.refund.error}`, type: 'error' });
      } else {
        setRefundToast({ message: 'Appointment cancelled.', type: 'success' });
      }
      setTimeout(() => setRefundToast(null), 7000);
    } catch (err) {
      console.error("Cancel failed", err);
      const errMsg = err.response?.data?.error || "Failed to cancel appointment";
      alert(errMsg);
    } finally {
      setRefundProcessing(false);
    }
  };

  const handleEdit = (appt) => {
    handleCloseMenu();
    if (appt.has_consultation) {
      alert("This appointment cannot be edited because a consultation already exists for it.");
      return;
    }
    navigate("/appointment-edit/", { state: appt });
  };

  const parseApptDate = (dateStr) => {
    try {
      let d;
      if (typeof dateStr === "string") {
        const clean = dateStr.trim();
        const dashParts = clean.slice(0, 10).split("-");
        if (dashParts.length === 3) {
          if (dashParts[0].length === 4) {
            d = new Date(parseInt(dashParts[0], 10), parseInt(dashParts[1], 10) - 1, parseInt(dashParts[2], 10));
          } else if (dashParts[2].length === 4) {
            d = new Date(parseInt(dashParts[2], 10), parseInt(dashParts[1], 10) - 1, parseInt(dashParts[0], 10));
          }
        }
        if (!d) {
          const slashParts = clean.slice(0, 10).split("/");
          if (slashParts.length === 3) {
            if (slashParts[0].length === 4) {
              d = new Date(parseInt(slashParts[0], 10), parseInt(slashParts[1], 10) - 1, parseInt(slashParts[2], 10));
            } else if (slashParts[2].length === 4) {
              d = new Date(parseInt(slashParts[2], 10), parseInt(slashParts[1], 10) - 1, parseInt(slashParts[0], 10));
            }
          }
        }
        if (!d || isNaN(d.getTime())) {
          d = new Date(dateStr);
        }
      } else {
        d = new Date(dateStr);
      }
      if (isNaN(d.getTime())) return null;
      d.setHours(0, 0, 0, 0);
      return d;
    } catch (e) {
      return null;
    }
  };

  const isPastDate = (dateStr) => {
    if (!dateStr) return false;
    const d = parseApptDate(dateStr);
    if (!d) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today;
  };

  // Both Start Consultation and Join Meeting are only available from 15
  // minutes before the scheduled start until the doctor's own slot
  // duration has elapsed - never before, never indefinitely after (a
  // doctor can't start/document a session hours late against the wrong
  // window, and can't join a call that's already over). duration_minutes
  // is snapshotted server-side from the doctor's slot_duration at booking
  // time (see AppointmentModel).
  const isConsultationWindowOpen = (a) => {
    if (!a.date || !a.time) return false;
    const start = new Date(`${a.date}T${a.time}`);
    if (isNaN(start.getTime())) return false;
    const minutesUntilStart = (start - new Date()) / 60000;
    const end = new Date(start.getTime() + (a.duration_minutes || 30) * 60000);
    return minutesUntilStart <= 15 && new Date() < end;
  };

  // Confirming Start Consultation creates the consultation record right
  // away (status "in_progress") rather than waiting for the doctor's
  // eventual Save inside the consultation form - this is what lets Join
  // Meeting become available immediately, both back on this listing page
  // and on the consultation add page itself, instead of only after the
  // doctor finishes writing notes. api.createConsultation is idempotent
  // (session/consultation.py::create_consultation) - the consultation
  // form's own later Save just updates this same record, it never creates
  // a second one.
  const confirmStartConsultation = async () => {
    if (!startConsultationAppt) return;
    const appt = startConsultationAppt;
    if (!isConsultationWindowOpen(appt)) {
      alert(
        "Consultations can only be started from 15 minutes before the scheduled time until the appointment's duration has elapsed."
      );
      setStartConsultationAppt(null);
      return;
    }
    setStartingConsultation(true);
    try {
      const res = await api.createConsultation({
        doctor_id: appt.doctor_id,
        patient_id: appt.patient_id,
        meeting_id: appt.id,
      });
      const patch = {
        has_consultation: true,
        consultation_id: res.data.id,
        consultation_status: res.data.status || "in_progress",
      };
      const updatedAppt = { ...appt, ...patch };
      setAppointments((prev) => prev.map((a) => (a.id === appt.id ? { ...a, ...patch } : a)));
      setFiltered((prev) => prev.map((a) => (a.id === appt.id ? { ...a, ...patch } : a)));
      fetchAppointments(); // background refresh for the fully server-resolved shape
      setStartConsultationAppt(null);
      navigate(`/Consultation-new`, { state: updatedAppt });
    } catch (err) {
      console.error("Failed to start consultation", err);
      alert(err.response?.data?.error || "Failed to start consultation. Please try again.");
    } finally {
      setStartingConsultation(false);
    }
  };

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-xl sm:text-2xl font-bold">Appointments</h1>
        <Link to="/appointment-new">
          <button
            className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            data-label="New Appointment"
          >
            + New Appointment
          </button>
        </Link>
      </div>

      {/* Filters and View Toggle */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
          <input
            type="text"
            placeholder="Search by name or phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-gray-300 p-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-56"
            data-label="Search Appointment"
          />
          <input
            type="text"
            placeholder="Filter by Doctor"
            value={doctorFilter}
            onChange={(e) => setDoctorFilter(e.target.value)}
            className="border border-gray-300 p-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-48"
            data-label="Filter by Doctor"
          />
          <PeriodPicker
            options={APPOINTMENT_PERIOD_OPTIONS}
            period={period}
            onPeriodChange={setPeriod}
            customStart={customStart}
            onCustomStartChange={setCustomStart}
            customEnd={customEnd}
            onCustomEndChange={setCustomEnd}
          />
          <label className="flex items-center gap-2 text-sm text-gray-600 select-none cursor-pointer px-2">
            <input
              type="checkbox"
              checked={showCancelled}
              onChange={(e) => setShowCancelled(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              data-label="Show Cancelled"
            />
            Show Cancelled
          </label>
        </div>

        {/* Toggle */}
        <div className="flex bg-gray-100 p-1 rounded-xl self-end md:self-auto border border-gray-200 shadow-inner">
          <button
            onClick={() => setViewMode('tabular')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${viewMode === 'tabular' ? 'bg-white shadow text-blue-700' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-200/50'}`}
          >
            <List className="w-4 h-4" />
            Tabular
          </button>
          <button
            onClick={() => setViewMode('calendar')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${viewMode === 'calendar' ? 'bg-white shadow text-blue-700' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-200/50'}`}
          >
            <Calendar className="w-4 h-4" />
            Calendar
          </button>
        </div>
      </div>

      {/* View Toggle */}
      {viewMode === 'calendar' ? (
        <CalendarView
          appointments={filtered}
          onReschedule={async (appt, start) => {
            const pad = (n) => String(n).padStart(2, "0");
            const newDate = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`;
            const newTime = `${pad(start.getHours())}:${pad(start.getMinutes())}`;
            const label = start.toLocaleString("en-IN", { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
            if (!window.confirm(`Move ${appt.patient_name}'s appointment to ${label}? They'll be told about the new time.`)) return;
            try {
              await api.rescheduleAppointment(appt.id, { new_date: newDate, new_time: newTime });
              fetchAppointments();
            } catch (err) {
              alert(err.response?.data?.error || "Couldn't move the appointment.");
            }
          }}
          onSlotSelect={(dateObj) => {
            const year = dateObj.getFullYear();
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const day = String(dateObj.getDate()).padStart(2, '0');
            const dateStr = `${year}-${month}-${day}`;

            let timeStr = "";
            if (dateObj.getHours() > 0 || dateObj.getMinutes() > 0) {
              const h = String(dateObj.getHours()).padStart(2, '0');
              const m = String(dateObj.getMinutes()).padStart(2, '0');
              timeStr = `${h}:${m}`;
            }
            navigate(`/appointment-new?date=${dateStr}${timeStr ? `&time=${timeStr}` : ''}`);
          }}
          onAppointmentSelect={(appt) => {
            if (appt.has_consultation && appt.consultation_id) {
              navigate(`/consultation/${appt.consultation_id}`);
            } else if (!isPastDate(appt.date) && appt.confirmation_status !== "Pending Patient Acceptance") {
              setStartConsultationAppt(appt);
            }
          }}
        />
      ) : (
        <>
          <div className="hidden md:block bg-white shadow-soft rounded-xl overflow-x-auto border border-secondary-100">
            <div className="p-4">
              {loading ? (
                <div className="p-8 text-center text-secondary-500 text-sm font-medium">
                  Loading appointments...
                </div>
              ) : filtered.length === 0 ? (
                <div className="p-8 text-center text-secondary-500 text-sm font-medium">
                  No appointments found matching your criteria.
                </div>
              ) : (
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-secondary-200">
                      <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">ID</th>
                      <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">Date & Time</th>
                      <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">Patient</th>
                      <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">Phone</th>
                      <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">Doctor</th>
                      <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">Type & Payment</th>
                      <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider text-right pr-6">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((a) => (
                      <tr key={a.id} id={`appt-row-${a.id}`} className={`border-b border-secondary-100 transition-colors relative ${a.id === highlightId ? "bg-amber-50 outline outline-2 outline-amber-400 -outline-offset-2" : a.is_cancelled ? "bg-red-50/40" : "hover:bg-secondary-50"}`}>
                        <td className="py-3 px-4 text-sm text-secondary-900 font-medium">{a.id}</td>
                        <td className="py-3 px-4 text-sm text-secondary-900 font-medium whitespace-nowrap">{a.date}<div className="text-xs text-secondary-500">{a.time}</div></td>
                        <td className="py-3 px-4 text-sm text-secondary-900 font-medium">{a.patient_name}</td>
                        <td className="py-3 px-4 text-sm text-secondary-900 font-medium">{a.patient_phone}</td>
                        <td className="py-3 px-4 text-sm text-secondary-900 font-medium">{a.doctor_name}</td>
                        <td className="py-3 px-4 text-sm text-secondary-900 font-medium">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <VisitTypeIcon appt={a} />{a.series_id && <button type="button" onClick={(e) => { e.stopPropagation(); setSeriesId(a.series_id); }} title={a.series_position ? `Visit ${a.series_position} of ${a.series_total} in a repeating series - see all visits` : "One of a repeating series - see all visits"} className="inline-flex items-center gap-0.5 rounded-full bg-primary-50 text-primary-700 px-2 py-0.5 text-[10px] font-bold uppercase hover:bg-primary-100"><Repeat className="w-3 h-3" aria-hidden="true" /> {a.series_position ? `${a.series_position}/${a.series_total}` : "Repeats"}</button>}
                            {a.is_cancelled && (
                              <span title={[a.cancelled_by && `Cancelled by ${a.cancelled_by}`, a.cancel_reason].filter(Boolean).join(": ") || "Cancelled"}
                                className="inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-200 cursor-help">
                                Cancelled{a.cancelled_by === "patient" ? " by patient" : ""}
                              </span>
                            )}
                            {a.attendance === "no_show" && (
                              <span title="No-show - patient didn't attend" aria-label="No-show" className="inline-flex items-center justify-center w-6 h-6 rounded-full shrink-0 bg-rose-100 text-rose-700 border border-rose-200"><UserX size={13} aria-hidden="true" /></span>
                            )}
                            {a.payment_status === "Prepaid" ? (
                              <button
                                type="button"
                                onClick={() => setSelectedPaymentAppt(a)}
                                className="relative inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 cursor-pointer hover:bg-emerald-200 transition-colors shrink-0"
                                title={a.collected_payment_method
                                  ? `Paid at clinic via ${a.collected_payment_method}${a.collected_payment_reference ? ` • Ref: ${a.collected_payment_reference}` : ''} - Click for details`
                                  : `Prepaid online${a.transaction_id ? ` • Txn: ${a.transaction_id}` : ''}${a.order_id ? ` • Order: ${a.order_id}` : ''} - Click for details`}
                                aria-label={a.collected_payment_method ? "Paid at clinic" : "Prepaid online"}
                              >
                                {a.collected_payment_method ? <Coins size={13} aria-hidden="true" /> : <IndianRupee size={13} aria-hidden="true" />}<span className="absolute -top-1 -right-1 flex items-center justify-center w-3 h-3 rounded-full bg-emerald-600 text-white ring-1 ring-white"><Check size={8} strokeWidth={4} aria-hidden="true" /></span>
                              </button>
                            ) : a.confirmation_status === "Declined" ? (
                              <span title={`${a.payment_mode === "Online" ? "Unpaid (online)" : "Pay at clinic"}${a.fee ? ` - ₹${a.fee}` : ""}`} aria-label={`${a.payment_mode === "Online" ? "Unpaid (online)" : "Pay at clinic"}${a.fee ? ` - ₹${a.fee}` : ""}`} className="relative inline-flex items-center justify-center w-6 h-6 rounded-full shrink-0 bg-amber-50 text-amber-600 border border-amber-100">
                                <IndianRupee size={13} aria-hidden="true" /><span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-500 ring-1 ring-white" />
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setSelectedPaymentAppt(a)}
                                className="relative inline-flex items-center justify-center w-6 h-6 rounded-full shrink-0 bg-amber-100 text-amber-700 border border-amber-200 cursor-pointer hover:bg-amber-200 transition-colors"
                                title={`${a.payment_mode === "Online" ? "Unpaid - patient hasn't paid online yet" : "Payment due at clinic"}${a.fee ? ` (₹${a.fee})` : ""} - Click for details`}
                                aria-label={`${a.payment_mode === "Online" ? "Unpaid (online)" : "Pay at clinic"}${a.fee ? ` - ₹${a.fee}` : ""}`}
                              >
                                <IndianRupee size={13} aria-hidden="true" /><span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-500 ring-1 ring-white" />
                              </button>
                            )}
                            {a.confirmation_status === "Pending Patient Acceptance" && (
                              <span
                                className="relative inline-flex items-center justify-center w-6 h-6 rounded-full bg-orange-100 text-orange-700 border border-orange-200 cursor-default"
                                title="Awaiting patient confirmation - they haven't accepted this appointment yet"
                              >
                                <Clock size={13} />
                                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-orange-500 animate-pulse ring-1 ring-white" />
                              </span>
                            )}
                            {a.confirmation_status === "Declined" && (
                              <span
                                className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-rose-100 text-rose-700 border border-rose-200 cursor-default"
                                title="The patient declined this appointment"
                              >
                                <XCircle size={13} />
                              </span>
                            )}
                            {a.has_consultation && (
                              a.consultation_status === "completed" ? (
                                <span title="Consultation completed" aria-label="Consultation completed" className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-700 border border-blue-200 shrink-0">
                                  <CheckCircle2 size={13} aria-hidden="true" />
                                </span>
                              ) : (
                                <span title="Consultation in progress" aria-label="Consultation in progress" className="relative inline-flex items-center justify-center w-6 h-6 rounded-full shrink-0 bg-yellow-100 text-yellow-700 border border-yellow-300">
                                  <Activity size={13} aria-hidden="true" /><span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-yellow-500 animate-pulse ring-1 ring-white" />
                                </span>
                              )
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-right pr-4 relative">
                          <div className="inline-block text-left">
                            <button
                              className="relative p-1.5 rounded-lg hover:bg-secondary-100 text-secondary-600 hover:text-secondary-900 transition-colors cursor-pointer"
                              onClick={(e) => toggleMenu(e, a.id)}
                              title="Actions"
                            >
                              <MoreVertical size={18} />
                              {a.unread_patient_messages > 0 && (
                                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-rose-500 ring-2 ring-white" />
                              )}
                            </button>

                            {openMenuId === a.id &&
                              createPortal(
                                <div
                                  ref={menuRef}
                                  className="fixed bg-white border border-secondary-200 rounded-xl shadow-xl z-50 min-w-[185px] py-1.5 animate-in fade-in zoom-in-95 duration-100"
                                  style={{
                                    top: `${menuPosition.top}px`,
                                    right: `${menuPosition.right}px`,
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {a.has_consultation ? (
                                    a.consultation_id ? (
                                      <>
                                        <button
                                          onClick={() => {
                                            handleCloseMenu();
                                            navigate(`/consultation/${a.consultation_id}`);
                                          }}
                                          className="flex items-center gap-2 w-full text-left px-4 py-2 hover:bg-secondary-50 transition-colors text-primary-700 font-medium text-sm cursor-pointer"
                                        >
                                          <FileText size={14} /> View Consultation
                                        </button>
                                        {(a.appttype || "").toLowerCase() === "online" && a.meeting_room_name && a.consultation_status !== "completed" && isConsultationWindowOpen(a) && (
                                          <button
                                            onClick={() => {
                                              handleCloseMenu();
                                              setVideoCallAppt(a);
                                            }}
                                            className="flex items-center gap-2 w-full text-left px-4 py-2 hover:bg-blue-50 transition-colors text-blue-700 font-medium text-sm cursor-pointer"
                                          >
                                            <Video size={14} />
                                            Join Meeting
                                          </button>
                                        )}
                                      </>
                                    ) : (
                                      <span className="block px-4 py-2 text-xs text-secondary-500 italic">
                                        Consultation completed
                                      </span>
                                    )
                                  ) : a.confirmation_status === "Declined" ? (
                                    <span className="block px-4 py-2 text-xs text-secondary-500 italic">
                                      Declined by patient
                                    </span>
                                  ) : (
                                    <>
                                      <button
                                        onClick={() => openCancelModal(a)}
                                        className="flex items-center gap-2 w-full text-left px-4 py-2 hover:bg-red-50 transition-colors text-sm font-medium text-red-600 cursor-pointer"
                                      >
                                        <XCircle size={14} /> Cancel
                                      </button>
                                      <button
                                        onClick={() => handleEdit(a)}
                                        className="flex items-center gap-2 w-full text-left px-4 py-2 hover:bg-secondary-50 transition-colors text-sm font-medium text-secondary-800 cursor-pointer"
                                      >
                                        <Pencil size={14} /> Edit
                                      </button>
                                      {canMarkNoShow(a) && (
                                        <button onClick={() => setAttendance(a, "no_show")}
                                          className="flex items-center gap-2 w-full text-left px-4 py-2 hover:bg-rose-50 transition-colors text-sm font-medium text-rose-700 cursor-pointer">
                                          <UserX size={14} /> Mark no-show
                                        </button>
                                      )}
                                      {a.attendance === "no_show" && features.has("attendance") && (
                                        <button onClick={() => setAttendance(a, "attended")}
                                          className="flex items-center gap-2 w-full text-left px-4 py-2 hover:bg-secondary-50 transition-colors text-sm font-medium text-secondary-800 cursor-pointer">
                                          <Undo2 size={14} /> Undo no-show
                                        </button>
                                      )}
                                      {a.confirmation_status !== "Pending Patient Acceptance" && isConsultationWindowOpen(a) && (
                                        <button
                                          onClick={() => {
                                            handleCloseMenu();
                                            setStartConsultationAppt(a);
                                          }}
                                          className="flex items-center gap-2 w-full text-left px-4 py-2 hover:bg-green-50 transition-colors text-green-700 font-medium text-sm cursor-pointer"
                                        >
                                          <PlayCircle size={14} /> Start Consultation
                                        </button>
                                      )}
                                    </>
                                  )}
                                  <div className="border-t border-secondary-100 my-1"></div>
                                  {a.confirmation_status !== "Declined" && (
                                    <button
                                      onClick={() => {
                                        handleCloseMenu();
                                        setSelectedPaymentAppt(a);
                                      }}
                                      className="flex items-center gap-2 w-full text-left px-4 py-2 hover:bg-secondary-50 transition-colors text-sm font-medium text-secondary-700 cursor-pointer"
                                      data-label="Payment Details"
                                    >
                                      <CreditCard size={14} className="text-secondary-500" />
                                      Payment Details
                                    </button>
                                  )}
                                  <button
                                    onClick={() => openMessagesModal(a)}
                                    className="flex items-center gap-2 w-full text-left px-4 py-2 hover:bg-secondary-50 transition-colors text-sm font-medium text-secondary-700 cursor-pointer"
                                    data-label="Messages"
                                  >
                                    <MessageSquare size={14} className="text-secondary-500" />
                                    Messages
                                    {a.unread_patient_messages > 0 && (
                                      <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700">
                                        {a.unread_patient_messages}
                                      </span>
                                    )}
                                  </button>
                                </div>,
                                document.body
                              )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          {/* Mobile Card View */}
          <div className="md:hidden space-y-4">
            {loading ? (
              <div className="bg-white shadow rounded-lg p-6 text-center">
                <p className="text-gray-600">Loading appointments...</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="bg-white shadow rounded-lg p-6 text-center">
                <p className="text-gray-600">No appointments found.</p>
              </div>
            ) : (
              filtered.map((a) => (
                <div
                  key={a.id}
                  id={`appt-card-${a.id}`}
                  className={`bg-white shadow rounded-lg p-4 border ${a.id === highlightId ? "border-amber-400 ring-2 ring-amber-300 bg-amber-50" : a.is_cancelled ? "border-red-200 bg-red-50/40" : "border-gray-200"}`}
                >
                  <div className="space-y-3">
                    <div className="flex items-center justify-between border-b pb-2">
                      <span className="text-xs text-gray-500 font-medium">ID</span>
                      <span className="text-sm font-semibold text-gray-900">{a.id}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 font-medium">Date & Time</span>
                      <span className="text-sm text-gray-900">{a.date} {a.time}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 font-medium">Patient</span>
                      <span className="text-sm text-gray-900">{a.patient_name}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 font-medium">Phone</span>
                      <span className="text-sm text-gray-900">{a.patient_phone}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 font-medium">Doctor</span>
                      <span className="text-sm text-gray-900">{a.doctor_name}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 font-medium">Type & Payment</span>
                      <div className="flex items-center gap-1.5 flex-wrap justify-end">
                        <VisitTypeIcon appt={a} />{a.series_id && <button type="button" onClick={(e) => { e.stopPropagation(); setSeriesId(a.series_id); }} title={a.series_position ? `Visit ${a.series_position} of ${a.series_total} in a repeating series - see all visits` : "One of a repeating series - see all visits"} className="inline-flex items-center gap-0.5 rounded-full bg-primary-50 text-primary-700 px-2 py-0.5 text-[10px] font-bold uppercase hover:bg-primary-100"><Repeat className="w-3 h-3" aria-hidden="true" /> {a.series_position ? `${a.series_position}/${a.series_total}` : "Repeats"}</button>}
                        {a.is_cancelled && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700 border border-red-200">
                            Cancelled
                          </span>
                        )}
                        {a.payment_status === "Prepaid" ? (
                          <button
                            type="button"
                            onClick={() => setSelectedPaymentAppt(a)}
                            className="relative inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 cursor-pointer hover:bg-emerald-200 transition-colors shrink-0"
                            title={a.collected_payment_method
                              ? `Paid at clinic via ${a.collected_payment_method} - Click for details`
                              : a.transaction_id ? `Txn ID: ${a.transaction_id} - Click for details` : "Prepaid online - Click for details"}
                            aria-label={a.collected_payment_method ? "Paid at clinic" : "Prepaid online"}
                          >
                            {a.collected_payment_method ? <Coins size={13} aria-hidden="true" /> : <IndianRupee size={13} aria-hidden="true" />}<span className="absolute -top-1 -right-1 flex items-center justify-center w-3 h-3 rounded-full bg-emerald-600 text-white ring-1 ring-white"><Check size={8} strokeWidth={4} aria-hidden="true" /></span>
                          </button>
                        ) : a.confirmation_status === "Declined" ? (
                          <span title={`${a.payment_mode === "Online" ? "Unpaid (online)" : "Pay at clinic"}${a.fee ? ` - ₹${a.fee}` : ""}`} aria-label={`${a.payment_mode === "Online" ? "Unpaid (online)" : "Pay at clinic"}${a.fee ? ` - ₹${a.fee}` : ""}`} className="relative inline-flex items-center justify-center w-6 h-6 rounded-full shrink-0 bg-amber-50 text-amber-600 border border-amber-100">
                            <IndianRupee size={13} aria-hidden="true" /><span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-500 ring-1 ring-white" />
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setSelectedPaymentAppt(a)}
                            className="relative inline-flex items-center justify-center w-6 h-6 rounded-full shrink-0 bg-amber-100 text-amber-700 border border-amber-200 cursor-pointer hover:bg-amber-200 transition-colors"
                            title={`${a.payment_mode === "Online" ? "Unpaid - patient hasn't paid online yet" : "Payment due at clinic"}${a.fee ? ` (₹${a.fee})` : ""} - Click for details`}
                            aria-label={`${a.payment_mode === "Online" ? "Unpaid (online)" : "Pay at clinic"}${a.fee ? ` - ₹${a.fee}` : ""}`}
                          >
                            <IndianRupee size={13} aria-hidden="true" /><span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-500 ring-1 ring-white" />
                          </button>
                        )}
                        {a.confirmation_status === "Pending Patient Acceptance" && (
                          <span
                            className="relative inline-flex items-center justify-center w-5 h-5 rounded-full bg-orange-100 text-orange-700 border border-orange-200"
                            title="Awaiting patient confirmation - they haven't accepted this appointment yet"
                          >
                            <Clock size={11} />
                            <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse ring-1 ring-white" />
                          </span>
                        )}
                        {a.confirmation_status === "Declined" && (
                          <span
                            className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-rose-100 text-rose-700 border border-rose-200"
                            title="The patient declined this appointment"
                          >
                            <XCircle size={11} />
                          </span>
                        )}
                        {a.has_consultation && (
                          a.consultation_status === "completed" ? (
                            <span title="Consultation completed" aria-label="Consultation completed" className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-700 border border-blue-200 shrink-0">
                              <CheckCircle2 size={13} aria-hidden="true" />
                            </span>
                          ) : (
                            <span title="Consultation in progress" aria-label="Consultation in progress" className="relative inline-flex items-center justify-center w-6 h-6 rounded-full shrink-0 bg-yellow-100 text-yellow-700 border border-yellow-300">
                                  <Activity size={13} aria-hidden="true" /><span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-yellow-500 animate-pulse ring-1 ring-white" />
                                </span>
                          )
                        )}
                      </div>
                    </div>

                    <div className="pt-2 border-t space-y-2">
                      {a.confirmation_status !== "Declined" && (
                        <button
                          type="button"
                          onClick={() => setSelectedPaymentAppt(a)}
                          className="flex items-center justify-center gap-1.5 w-full text-center bg-gray-50 border border-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors font-medium text-xs"
                        >
                          <CreditCard size={14} className="text-gray-500" />
                          View Payment & Ledger Info
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => openMessagesModal(a)}
                        className="relative flex items-center justify-center gap-1.5 w-full text-center bg-gray-50 border border-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors font-medium text-xs"
                      >
                        <MessageSquare size={14} className="text-gray-500" />
                        Messages
                        {a.unread_patient_messages > 0 && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700">
                            {a.unread_patient_messages}
                          </span>
                        )}
                      </button>

                      {a.has_consultation ? (
                        a.consultation_id ? (
                          <>
                            <button
                              onClick={() => navigate(`/consultation/${a.consultation_id}`)}
                              className="flex items-center justify-center gap-1.5 w-full text-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                              data-label="View Consultation"
                            >
                              <FileText size={14} /> View Consultation
                            </button>
                            {(a.appttype || "").toLowerCase() === "online" && a.meeting_room_name && a.consultation_status !== "completed" && isConsultationWindowOpen(a) && (
                              <button
                                onClick={() => setVideoCallAppt(a)}
                                className="flex items-center justify-center gap-1.5 w-full text-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                                data-label="Join Meeting"
                              >
                                <Video size={14} />
                                Join Meeting
                              </button>
                            )}
                          </>
                        ) : (
                          <p className="text-xs text-center text-gray-500 italic py-1">
                            Consultation completed (Cannot edit/cancel)
                          </p>
                        )
                      ) : a.confirmation_status === "Declined" ? (
                        <p className="text-xs text-center text-gray-500 italic py-1">
                          Declined by patient (Cannot edit/cancel)
                        </p>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEdit(a)}
                            className="flex items-center justify-center gap-1.5 w-full text-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                            data-label="Edit Appointment"
                          >
                            <Pencil size={14} /> Edit
                          </button>
                          {a.confirmation_status !== "Pending Patient Acceptance" && isConsultationWindowOpen(a) && (
                            <button
                              onClick={() => setStartConsultationAppt(a)}
                              className="flex items-center justify-center gap-1.5 w-full text-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
                              data-label="Start Consultation"
                            >
                              <PlayCircle size={14} /> Start Consultation
                            </button>
                          )}
                          {canMarkNoShow(a) && (
                            <button onClick={() => setAttendance(a, "no_show")}
                              className="flex items-center justify-center gap-1.5 w-full text-center border border-rose-300 text-rose-700 px-4 py-2 rounded-lg hover:bg-rose-50 transition-colors font-medium text-sm">
                              <UserX size={14} /> Mark no-show
                            </button>
                          )}
                          {a.attendance === "no_show" && features.has("attendance") && (
                            <button onClick={() => setAttendance(a, "attended")}
                              className="flex items-center justify-center gap-1.5 w-full text-center border border-secondary-300 text-secondary-800 px-4 py-2 rounded-lg font-medium text-sm">
                              <Undo2 size={14} /> Undo no-show
                            </button>
                          )}
                          <button
                            onClick={() => openCancelModal(a)}
                            className="flex items-center justify-center gap-1.5 w-full text-center bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors font-medium text-sm"
                            data-label="Cancel Appointment"
                          >
                            <XCircle size={14} /> Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

        </>
      )}

      {seriesId && <SeriesModal seriesId={seriesId} onClose={() => setSeriesId(null)} onChanged={fetchAppointments} />}
      {videoCallAppt && (
        <VideoCallModal appointment={videoCallAppt} onClose={(outcome) => {
          const appt = videoCallAppt;
          setVideoCallAppt(null);
          if (outcome) setAppointments((prev) => prev.map((x) => (x.id === appt.id ? { ...x, attendance: outcome === "no_show" ? "no_show" : "attended" } : x)));
          // After a held session, go straight to writing the note.
          if (outcome && outcome !== "no_show" && !appt.has_consultation) setStartConsultationAppt(appt);
        }} />
      )}

      {/* Start Consultation confirmation */}
      {startConsultationAppt && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs"
          onClick={() => !startingConsultation && setStartConsultationAppt(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-sm w-full shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5">
              <h3 className="font-semibold text-secondary-800 text-base mb-2">Start Consultation?</h3>
              <p className="text-sm text-secondary-600">
                This marks the consultation as in progress
                {(startConsultationAppt.appttype || "").toLowerCase() === "online"
                  ? " and makes the video call link available for this session."
                  : "."}
              </p>
            </div>
            <div className="flex border-t border-secondary-100">
              <button
                onClick={() => setStartConsultationAppt(null)}
                disabled={startingConsultation}
                className="flex-1 px-4 py-3 text-sm font-medium text-secondary-600 hover:bg-secondary-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmStartConsultation}
                disabled={startingConsultation}
                className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {startingConsultation ? "Starting..." : "Start Consultation"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Details Modal */}
      {selectedPaymentAppt && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs"
          onClick={() => setSelectedPaymentAppt(null)}
        >
          <div 
            className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/70">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-lg bg-primary-50 text-primary-600">
                  <CreditCard size={18} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Payment & Ledger Details</h3>
                  <p className="text-xs text-gray-500">Appointment #{selectedPaymentAppt.id}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedPaymentAppt(null)}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4 text-sm">
              {/* Status Banner */}
              <div className={`p-4 rounded-xl border flex items-center justify-between ${
                selectedPaymentAppt.payment_status === 'Prepaid'
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                  : 'bg-amber-50 border-amber-200 text-amber-900'
              }`}>
                <div>
                  <span className="text-[11px] uppercase tracking-wider font-semibold opacity-75">Status</span>
                  <div className="font-bold text-lg">
                    {selectedPaymentAppt.payment_status === 'Prepaid'
                      ? (selectedPaymentAppt.collected_payment_method ? 'Paid at Clinic' : 'Prepaid Online')
                      : (selectedPaymentAppt.payment_mode === 'Online' ? 'Online · Payment Pending' : 'Pay at Clinic')}
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-[11px] uppercase tracking-wider font-semibold opacity-75">Consultation Fee</span>
                  <div className="font-bold text-xl">
                    ₹{selectedPaymentAppt.fee || 0}
                  </div>
                </div>
              </div>

              {/* Confirmation status - only staff-created appointments ever
                  carry anything other than "Confirmed" (see
                  session/appointment.py::create_appointment) */}
              {selectedPaymentAppt.confirmation_status === 'Pending Patient Acceptance' && (
                <div className="p-3 rounded-xl border bg-orange-50 border-orange-200 text-orange-900 flex items-center gap-2 text-xs font-semibold">
                  <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse shrink-0" />
                  Awaiting patient confirmation - they haven't accepted this appointment yet.
                </div>
              )}
              {selectedPaymentAppt.confirmation_status === 'Declined' && (
                <div className="p-3 rounded-xl border bg-rose-50 border-rose-200 text-rose-900 flex items-center gap-2 text-xs font-semibold">
                  The patient declined this appointment.
                </div>
              )}

              {/* Payout Breakdown - only ever populated for a Prepaid appointment
                  with a settled Razorpay transaction; a Pay-at-Clinic collection
                  never passes through the gateway, so there's no deduction here
                  for this system to know about. */}
              {selectedPaymentAppt.payment_status === 'Prepaid' && selectedPaymentAppt.net_amount != null && (
                <div className="space-y-1.5 bg-primary-50/60 p-4 rounded-xl border border-primary-100">
                  <p className="text-[11px] font-semibold text-primary-900 uppercase tracking-wide mb-1">Payout Breakdown</p>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Amount Collected</span>
                    <span className="font-semibold text-gray-800">₹{selectedPaymentAppt.fee || 0}</span>
                  </div>
                  {selectedPaymentAppt.gateway_fee != null && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">Gateway Fee</span>
                      <span className="font-semibold text-red-600">- ₹{selectedPaymentAppt.gateway_fee}</span>
                    </div>
                  )}
                  {selectedPaymentAppt.gateway_tax != null && selectedPaymentAppt.gateway_tax > 0 && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">Tax on Gateway Fee (GST)</span>
                      <span className="font-semibold text-red-600">- ₹{selectedPaymentAppt.gateway_tax}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-1.5 mt-1 border-t border-primary-200/70">
                    <span className="text-xs font-bold text-primary-900">You Receive</span>
                    <span className="font-bold text-emerald-700">₹{selectedPaymentAppt.net_amount}</span>
                  </div>
                </div>
              )}

              {/* Identifiers & Ledger */}
              <div className="space-y-2.5">
                {(selectedPaymentAppt.order_id || selectedPaymentAppt.transaction_id) && (
                  <div className="bg-white border border-gray-200 rounded-xl grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
                    {selectedPaymentAppt.order_id && (
                      <div className="p-3 min-w-0">
                        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Order Reference</p>
                        <p className="text-xs font-mono font-semibold text-gray-800 mt-0.5 truncate" title={selectedPaymentAppt.order_id}>{selectedPaymentAppt.order_id}</p>
                      </div>
                    )}

                    {selectedPaymentAppt.transaction_id && (
                      <div className="p-3 min-w-0">
                        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Transaction ID</p>
                        <p className="text-xs font-mono font-semibold text-gray-800 mt-0.5 truncate" title={selectedPaymentAppt.transaction_id}>{selectedPaymentAppt.transaction_id}</p>
                      </div>
                    )}
                  </div>
                )}

                {selectedPaymentAppt.payment_status === "Prepaid" && selectedPaymentAppt.collected_payment_method ? (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-900 space-y-0.5">
                    <p className="font-semibold">
                      Collected in person via {selectedPaymentAppt.collected_payment_method}
                      {selectedPaymentAppt.collected_payment_reference ? ` • Ref: ${selectedPaymentAppt.collected_payment_reference}` : ""}
                    </p>
                    {(selectedPaymentAppt.collected_by_name || selectedPaymentAppt.collected_at) && (
                      <p className="text-emerald-700">
                        {selectedPaymentAppt.collected_by_name ? `by ${selectedPaymentAppt.collected_by_name}` : ""}
                        {selectedPaymentAppt.collected_by_name && selectedPaymentAppt.collected_at ? " · " : ""}
                        {selectedPaymentAppt.collected_at || ""}
                      </p>
                    )}
                  </div>
                ) : selectedPaymentAppt.payment_status !== "Prepaid" ? (
                  <div className="p-4 bg-amber-50/60 border border-amber-200 rounded-xl space-y-3">
                    <div>
                      <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide">Record Payment Collected</p>
                      <p className="text-xs text-gray-600 mt-0.5">Mark this appointment paid once you've collected the fee in person.</p>
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Amount Collected (₹)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={collectAmount}
                        onChange={(e) => setCollectAmount(e.target.value)}
                        placeholder="0.00"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <select
                        value={collectMethod}
                        onChange={(e) => setCollectMethod(e.target.value)}
                        className="px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 bg-white"
                      >
                        <option value="">Select method…</option>
                        <option value="Cash">Cash</option>
                        <option value="Card">Card (POS)</option>
                        <option value="UPI">UPI</option>
                        <option value="Cheque">Cheque</option>
                        <option value="Other">Other</option>
                      </select>
                      <input
                        type="text"
                        value={collectReference}
                        onChange={(e) => setCollectReference(e.target.value)}
                        placeholder="Reference (optional)"
                        className="px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    {collectError && <p className="text-xs text-red-600">{collectError}</p>}
                    <button
                      type="button"
                      onClick={handleCollectPayment}
                      disabled={!collectMethod || collecting}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
                    >
                      {collecting ? "Recording…" : `Record ₹${collectAmount || 0} Collected`}
                    </button>
                  </div>
                ) : !selectedPaymentAppt.transaction_id ? (
                  <div className="p-3 bg-gray-50 border border-dashed border-gray-200 rounded-xl text-center">
                    <p className="text-xs text-gray-500">No online transaction recorded</p>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex justify-end">
              <button
                type="button"
                onClick={() => setSelectedPaymentAppt(null)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-xl text-xs transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Messages Modal */}
      {selectedMessagesAppt && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs"
          onClick={() => setSelectedMessagesAppt(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150 flex flex-col max-h-[85vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/70 flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-lg bg-primary-50 text-primary-600">
                  <MessageSquare size={18} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Messages</h3>
                  <p className="text-xs text-gray-500">{selectedMessagesAppt.patient_name} — Appointment #{selectedMessagesAppt.id}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedMessagesAppt(null)}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50/40">
              {messagesLoading ? (
                <p className="text-xs text-center text-gray-400 py-6">Loading messages…</p>
              ) : messages.length === 0 ? (
                <p className="text-xs text-center text-gray-400 py-6">No messages yet. Start the conversation below.</p>
              ) : (
                messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender_type === "doctor" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-xl px-3 py-2 text-xs whitespace-pre-wrap ${
                      m.sender_type === "doctor" ? "bg-primary-600 text-white" : "bg-white border border-gray-200 text-gray-800"
                    }`}>
                      <p className={`text-[10px] font-semibold mb-0.5 ${m.sender_type === "doctor" ? "text-primary-100" : "text-amber-600"}`}>
                        {m.sender_type === "doctor" ? "You" : "From Patient"}
                      </p>
                      <p>{m.body}</p>
                      <p className={`text-[9px] mt-1 ${m.sender_type === "doctor" ? "text-primary-200" : "text-gray-400"}`}>
                        {m.created_at ? new Date(m.created_at).toLocaleString() : ""}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="p-3 border-t border-gray-100 bg-white flex-shrink-0 space-y-2">
              <div className="flex justify-end">
                {/* <button
                  type="button"
                  onClick={insertSoapTemplate}
                  className="text-[10px] font-semibold text-primary-600 hover:text-primary-800 hover:underline"
                >
                  Insert SOAP Template
                </button> */}
              </div>
              <div className="flex items-end gap-2">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message…"
                  rows={2}
                  className="flex-1 px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 resize-none"
                />
                <button
                  type="button"
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || sendingMessage}
                  className="p-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white transition-colors"
                  title="Send"
                >
                  <Send size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Refund Confirmation Modal */}
      {refundModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs" onClick={() => !refundProcessing && setRefundModal(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-red-50">
              <div>
                <h3 className="text-base font-bold text-red-900">Cancel Appointment</h3>
                <p className="text-xs text-red-600 mt-0.5">{refundModal.appt.patient_name} — {refundModal.appt.date}</p>
              </div>
              <button onClick={() => !refundProcessing && setRefundModal(null)} className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100">
                <X size={18} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1.5">Reason <span className="text-xs font-normal text-gray-500">(the patient sees this)</span></p>
                <div className="flex flex-wrap gap-1.5">
                  {CANCEL_REASONS.map((r) => (
                    <button key={r} type="button" onClick={() => setRefundModal((m) => ({ ...m, reason: r }))}
                      className={`rounded-full border px-3 py-1 text-xs font-medium ${refundModal.reason === r ? "border-red-500 bg-red-50 text-red-800" : "border-gray-300 text-gray-700 hover:border-red-300"}`}>{r}</button>
                  ))}
                </div>
                {refundModal.reason === "Other" && (
                  <input value={refundModal.reasonText || ""} onChange={(e) => setRefundModal((m) => ({ ...m, reasonText: e.target.value }))} maxLength={300}
                    placeholder="Short reason" aria-label="Reason" className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                )}
              </div>
              {refundModal.isPrepaid ? (
                <>
                  <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
                    <span className="text-lg">⚠️</span>
                    <span>This appointment was <strong>prepaid online (₹{refundModal.appt.fee})</strong>. A refund will be initiated to the original payment source.</span>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Refund Amount (₹) <span className="text-xs font-normal text-gray-500">(Max: ₹{refundModal.appt.fee})</span>
                    </label>
                    <input
                      type="number"
                      min="0.01"
                      max={refundModal.appt.fee}
                      step="0.01"
                      value={refundModal.refundAmount}
                      onChange={e => {
                        const val = e.target.value;
                        setRefundModal(m => ({ ...m, refundAmount: val }));
                      }}
                      className={`w-full border rounded-xl px-4 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 ${
                        parseFloat(refundModal.refundAmount) > parseFloat(refundModal.appt.fee) || parseFloat(refundModal.refundAmount) <= 0 || isNaN(parseFloat(refundModal.refundAmount))
                          ? 'border-red-500 focus:ring-red-300 bg-red-50/50'
                          : 'border-gray-300 focus:ring-red-300'
                      }`}
                    />
                    {parseFloat(refundModal.refundAmount) > parseFloat(refundModal.appt.fee) ? (
                      <p className="text-xs text-red-600 font-semibold mt-1.5">
                        ❌ Amount cannot exceed the paid fee of ₹{refundModal.appt.fee}
                      </p>
                    ) : parseFloat(refundModal.refundAmount) <= 0 || isNaN(parseFloat(refundModal.refundAmount)) ? (
                      <p className="text-xs text-red-600 font-semibold mt-1.5">
                        ❌ Refund amount must be greater than ₹0
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500 mt-1">Default is 100% full refund. You can lower it for a partial refund.</p>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-700">Are you sure you want to cancel this appointment? This action cannot be undone.</p>
              )}
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
              <button onClick={() => setRefundModal(null)} disabled={refundProcessing} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-xl text-sm transition-colors disabled:opacity-50">Keep Appointment</button>
              <button
                onClick={handleConfirmCancel}
                disabled={
                  refundProcessing ||
                  (refundModal.isPrepaid && (
                    isNaN(parseFloat(refundModal.refundAmount)) ||
                    parseFloat(refundModal.refundAmount) <= 0 ||
                    parseFloat(refundModal.refundAmount) > parseFloat(refundModal.appt.fee)
                  ))
                }
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {refundProcessing && <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                {refundModal.isPrepaid ? `Cancel & Refund ₹${refundModal.refundAmount || 0}` : 'Cancel Appointment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refund Toast */}
      {refundToast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] px-5 py-3.5 rounded-2xl shadow-xl text-sm font-medium max-w-md text-center animate-in fade-in slide-in-from-bottom-4 duration-300 ${
          refundToast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {refundToast.message}
        </div>
      )}

      {/* Click outside to close menu */}
      {openMenuId && (
        <div
          className="fixed inset-0 z-40"
          onClick={handleCloseMenu}
        />
      )}
    </DashboardLayout>
  );
}
